/**
 * Toolbox-log entry DTOs.
 *
 * The toolbox log is a per-project derived view over Document +
 * DocumentVersion + ToolboxTalkSession + Signature rows. There is NO
 * dedicated Prisma table — the shape comes from the existing web route
 * `app/api/projects/[projectId]/toolbox-log/route.ts` which the mobile
 * read endpoint will reuse.
 *
 * Schema preserves the existing web response 1:1 so Phase 06 can lift the
 * implementation into the mobile namespace without reshaping.
 */
import { z } from "zod";
import { INT32_MAX, INT32_MIN, entityIdSchema } from "../common";

export const ToolboxLogAttendeeSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    role: z.string(),
    company: z.string().nullable(),
    signedAt: z.string().datetime(),
  });

export type ToolboxLogAttendee = z.infer<typeof ToolboxLogAttendeeSchema>;

/**
 * A single delivered toolbox talk. `documentId` ties the entry back to the
 * underlying Document row so mobile can deep-link into the document detail
 * screen.
 *
 * `questions` is intentionally `z.unknown()` because the persisted shape
 * varies between admin-curated topics (with multiple-choice options) and
 * user-authored topics (free-form QA pairs). Phase 11 narrows this when
 * the mobile toolbox screen lands.
 */
export const ToolboxLogEntrySchema = z
  .object({
    documentId: entityIdSchema,
    date: z.string().datetime(),
    topic: z.string(),
    deliveredBy: z.string(),
    durationMinutes: z.number().int().min(INT32_MIN).max(INT32_MAX).nullable(),
    keyPoints: z.array(z.string()),
    dos: z.array(z.string()),
    donts: z.array(z.string()),
    questions: z.unknown(),
    additionalNotes: z.string().nullable(),
    attendeeCount: z.number().int().min(0).max(INT32_MAX),
    attendees: z.array(ToolboxLogAttendeeSchema),
  });

export type ToolboxLogEntry = z.infer<typeof ToolboxLogEntrySchema>;

/**
 * GET /api/mobile/v1/projects/:projectId/toolbox-log response. Matches the
 * existing web route shape `{ log: ToolboxLogEntry[] }`.
 */
export const ToolboxLogResponse = z
  .object({
    log: z.array(ToolboxLogEntrySchema),
  });

export type ToolboxLogResponse = z.infer<typeof ToolboxLogResponse>;

/**
 * Server-only, full-depth strict variants of the three schemas above.
 *
 * The schemas the mobile app parses are deliberately NOT `.strict()` (issue
 * #2411) so an installed build tolerates a field the server added after it
 * shipped. That tolerance is a CLIENT property — on the server we still want
 * an over-serialisation guard, so a raw Prisma row handed to a serialiser
 * fails loudly instead of leaking every column onto the wire.
 *
 * `.strict()` only applies to the level it is called on, so re-stating it at
 * the response boundary needs the nested levels rebuilt too — hence these
 * explicit variants rather than a bare `ToolboxLogResponse.strict()`.
 */
export const ToolboxLogAttendeeStrict = ToolboxLogAttendeeSchema.strict();

export const ToolboxLogEntryStrict = ToolboxLogEntrySchema.extend({
  attendees: z.array(ToolboxLogAttendeeStrict),
}).strict();

export const ToolboxLogResponseStrict = z
  .object({
    log: z.array(ToolboxLogEntryStrict),
  })
  .strict();
